import { getMicroservicesClient } from '@/services/microservices/client';

const getMicroservicesBaseUrl = () => getMicroservicesClient('us').baseUrl;

const getTokenApiPath = () => `${getMicroservicesBaseUrl()}/account/token`;

const getDeviceInfoApiPath = () => `${getMicroservicesBaseUrl()}/device/info`;

const getNonPiiAccountApiPath = () => `${getMicroservicesBaseUrl()}/account/non-pii`;

export interface DeviceTokenRequestPayload {
    student: string;
    token: string;
    tokenType: string;
}

export interface DeviceInfoRequestPayload {
    radioId: string;
}

export interface NonPiiAccountRequestPayload {
    radioId: string;
    accountNumber: string;
}

export interface DeviceTokenResponse {
    vehicleYear?: string;
    vehicleMake?: string;
    vehicleModel?: string;
    last4DigitsOfRadioId?: string;
}

interface TokenApiVehicleInfo {
    vin?: string | null;
    year?: string;
    make?: string;
    model?: string;
}

interface TokenApiRadioService {
    vehicleInfo?: TokenApiVehicleInfo;
    last4DigitsOfRadioId?: string;
}

interface TokenApiSubscription {
    radioService?: TokenApiRadioService;
}
interface TokenApiClosedDevice {
    vehicleInfo?: TokenApiVehicleInfo;
    last4DigitsOfRadioId?: string;
    subscription?: {
        radioService?: TokenApiRadioService;
    };
}

interface TokenApiResponse {
    status: string;
    data?: {
        nonPIIAccount?: {
            subscriptions?: TokenApiSubscription[];
            closedDevices?: TokenApiClosedDevice[];
        };
    };
}

function parseVehicleInfoResponse(json: TokenApiResponse): DeviceTokenResponse | null {
    const nonPIIAccount = json.data?.nonPIIAccount;
    const activeSubscription = nonPIIAccount?.subscriptions?.find(
        (subscription) => subscription?.radioService?.vehicleInfo?.year,
    );
    const closedDevice = nonPIIAccount?.closedDevices?.[0];

    const vehicleInfo =
        activeSubscription?.radioService?.vehicleInfo ??
        (closedDevice?.vehicleInfo?.year ? closedDevice.vehicleInfo : closedDevice?.subscription?.radioService?.vehicleInfo);
    const radioId =
        activeSubscription?.radioService?.last4DigitsOfRadioId ??
        (closedDevice?.last4DigitsOfRadioId
            ? closedDevice.last4DigitsOfRadioId
            : closedDevice?.subscription?.radioService?.last4DigitsOfRadioId);

    if (!vehicleInfo && !radioId) {
        return null;
    }

    return {
        vehicleYear: vehicleInfo?.year,
        vehicleMake: vehicleInfo?.make,
        vehicleModel: vehicleInfo?.model,
        last4DigitsOfRadioId: radioId,
    };
}

export async function getDeviceToken(payload: DeviceTokenRequestPayload): Promise<DeviceTokenResponse | null> {
    try {
        const response = await fetch(getTokenApiPath(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token: payload.token,
                student: payload.student === 'Yes',
                tokenType: payload.tokenType,
            }),
        });
        if (!response.ok) return null;
        const json = (await response.json()) as TokenApiResponse;
        return parseVehicleInfoResponse(json);
    } catch {
        return null;
    }
}

export async function getDeviceInfo(payload: DeviceInfoRequestPayload): Promise<DeviceTokenResponse | null> {
    try {
        const response = await fetch(getDeviceInfoApiPath(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ radioId: payload.radioId }),
        });
        if (!response.ok) return null;
        const json = (await response.json()) as TokenApiResponse;
        return parseVehicleInfoResponse(json);
    } catch {
        return null;
    }
}


export async function getNonPiiAccount(payload: NonPiiAccountRequestPayload): Promise<DeviceTokenResponse | null> {
    try {
        const response = await fetch(getNonPiiAccountApiPath(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ radioId: payload.radioId, accountNumber: payload.accountNumber }),
        });
        if (!response.ok) return null;
        const json = (await response.json()) as TokenApiResponse;
        return parseVehicleInfoResponse(json);
    } catch {
        return null;
    }
}